<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="navigateToDashboard"/>

        <!-- Account Summary -->
        <div class="account-summary-container">
            <div class="account-summary">

                <!-- Row 1: Balance -->
                <div class="summary-row">
                    <div class="account-type">{{ $t('employer.customerFactors.balanceTitle') }}</div>
                    <div class="account-balance">
                        <span class="balance-amount">
                            {{ formatRemainingCash(remainingCash) }}
                            {{ formatBestanKari(remainingCash) }}
                        </span>
                    </div>
                </div>

                <!-- Row 2: Name + Tel -->
                <div class="summary-row second-row">
                    <div class="customer-info">
                        <span class="info-label">{{ $t('employer.customerFactors.name') }}:</span>
                        <span class="info-value">{{ nameCustomer || $t('employer.customerFactors.notSet') }}</span>
                    </div>
                    
                    <div class="customer-info tel-info">
                        <span class="info-label">{{ $t('employer.customerFactors.phone') }}:</span>
                        <span class="info-value">{{ telCustomer || $t('employer.customerFactors.notSet') }}</span>
                    </div>
                </div>

            </div>
        </div>

        <!-- Section Header -->
        <div class="section-header">
            <!-- <button class="back-arrow" @click="navigateToDashboard">→</button> -->
            <h3 class="section-title mt-3" style="margin-right: 10px;">{{ $t('employer.customerFactors.sectionTitle') }}</h3>
        </div>
        
        <!-- Action Bar -->
        <div class="action-bar">

            <!-- Print -->
            <button class="print-btn" @click="printData" :title="$t('employer.customerFactors.print')">
                🖨️
            </button>

            <!-- Sort -->
            <button 
                class="sort-btn"
                @click="toggleSortOrder"
                :title="sortOrder === 'desc'
                    ? $t('employer.customerFactors.sortAsc')
                    : $t('employer.customerFactors.sortDesc')">
                {{ sortOrder === 'desc' ? '⬇️' : '⬆️' }}
                <span class="sort-text">
                    {{ sortOrder === 'desc'
                        ? $t('employer.customerFactors.desc')
                        : $t('employer.customerFactors.asc') }}
                </span>
            </button>

            <!-- Filter Button -->
            <button 
                class="filter-btn"
                @click="toggleFilterPanel"
                :title="$t('employer.customerFactors.filters')"
            >
                🎛️
                <span class="filter-text">{{ $t('employer.customerFactors.filter') }}</span>
                <span v-if="activeFilterCount > 0" class="filter-badge">{{ activeFilterCount }}</span>
            </button>

            <!-- Currency -->
            <div class="currency-selector">
                <label for="currency-select" class="currency-label">{{ $t('employer.customerFactors.currency') }}:</label>
                
                <select
                    id="currency-select"
                    v-model="selectedCurrency"
                    @change="onCurrencyChange"
                    class="currency-select"
                    :disabled="loadingArz || arzList.length === 0"
                >
                    <option 
                        v-for="arz in arzList"
                        :key="arz.idd"
                        :value="arz.idd"
                        :selected="arz.idd === 1"
                    >
                        {{ arz.nam }} ({{ arz.sign }})
                    </option>
                </select>

                <div v-if="loadingArz" class="currency-loading">
                    {{ $t('employer.customerFactors.loadingCurrencies') }}
                </div>
            </div>

            <!-- Search Input -->
            <div class="search-container">
                <input
                    type="text"
                    v-model="searchQuery"
                    :placeholder="$t('employer.customerFactors.searchPlaceholder')"
                    class="search-input"
                />
                <span class="search-icon">🔍</span>
            </div>
        </div>

        <!-- Filter Panel -->
        <div v-if="showFilterPanel" class="filter-panel">
            <div class="filter-header">
                <h4>{{ $t('employer.customerFactors.filters') }}</h4>
                <button class="close-filter-btn" @click="closeFilterPanel">×</button>
            </div>

            <div class="filter-content">

                <!-- Transaction Type -->
                <div class="filter-group">
                    <label class="filter-label">{{ $t('employer.customerFactors.transactionType') }}:</label>

                    <div class="filter-options">

                        <label class="filter-option">
                            <input type="checkbox" v-model="filters.transactionType" value="debit" />
                            <span class="checkmark"></span>
                            {{ $t('employer.customerFactors.debit') }}
                        </label>

                        <label class="filter-option">
                            <input type="checkbox" v-model="filters.transactionType" value="credit" />
                            <span class="checkmark"></span>
                            {{ $t('employer.customerFactors.credit') }}
                        </label>

                    </div>
                </div>

                <!-- Date Range -->
                <div class="filter-group">
                    <label class="filter-label">{{ $t('employer.customerFactors.dateRange') }}:</label>

                    <div class="date-inputs">
                        <div class="date-input">
                            <label>{{ $t('employer.customerFactors.dateFrom') }}:</label>
                            <DatePicker
                                v-model="filters.dateFrom"
                                format="jYYYY/jMM/jDD"
                                display-format="jYYYY/jMM/jDD"
                                :placeholder="$t('employer.customerFactors.pickDate')"
                                input-class="persian-date-picker"
                                :auto-submit="true"
                            />
                        </div>

                        <div class="date-input">
                            <label>{{ $t('employer.customerFactors.dateTo') }}:</label>
                            <DatePicker
                                v-model="filters.dateTo"
                                format="jYYYY/jMM/jDD"
                                display-format="jYYYY/jMM/jDD"
                                :placeholder="$t('employer.customerFactors.pickDate')"
                                input-class="persian-date-picker"
                                :auto-submit="true"
                            />
                        </div>
                    </div>
                </div>

                <!-- Amount Range -->
                <div class="filter-group">
                    <label class="filter-label">{{ $t('employer.customerFactors.amountRange') }} ({{ $t('employer.customerFactors.currencyUnit') }}):</label>

                    <div class="amount-inputs">

                        <div class="amount-input">
                            <label>{{ $t('employer.customerFactors.amountFrom') }}:</label>
                            <input
                                type="number"
                                v-model="filters.amountFrom"
                                placeholder="0"
                                class="amount-picker"
                            >
                        </div>

                        <div class="amount-input">
                            <label>{{ $t('employer.customerFactors.amountTo') }}:</label>
                            <input
                                type="number"
                                v-model="filters.amountTo"
                                placeholder="∞"
                                class="amount-picker"
                            >
                        </div>
                    </div>
                </div>

                <!-- Filter Actions -->
                <div class="filter-actions">
                    <button class="apply-filters-btn" @click="applyFilters">
                        {{ $t('employer.customerFactors.applyFilters') }}
                    </button>

                    <button class="clear-filters-btn" @click="clearFilters">
                        {{ $t('employer.customerFactors.clearFilters') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Active Filters -->
        <div v-if="activeFilterCount > 0" class="active-filters">
            <div class="active-filters-header">
                <span>{{ $t('employer.customerFactors.activeFilters') }}:</span>
                <button class="clear-all-filters" @click="clearFilters">{{ $t('employer.customerFactors.clearAll') }}</button>
            </div>

            <div class="active-filter-tags">

                <span v-if="filters.transactionType.length > 0" class="filter-tag">
                    {{ $t('employer.customerFactors.type') }}:
                    {{ getTransactionTypeText() }}
                    <button @click="removeTransactionTypeFilter">×</button>
                </span>

                <span v-if="filters.dateFrom" class="filter-tag">
                    {{ $t('employer.customerFactors.dateFrom') }}: {{ formatFilterDate(filters.dateFrom) }}
                    <button @click="filters.dateFrom = ''">×</button>
                </span>

                <span v-if="filters.dateTo" class="filter-tag">
                    {{ $t('employer.customerFactors.dateTo') }}: {{ formatFilterDate(filters.dateTo) }}
                    <button @click="filters.dateTo = ''">×</button>
                </span>

                <span v-if="filters.amountFrom" class="filter-tag">
                    {{ $t('employer.customerFactors.amountFrom') }}: {{ formatCurrency(filters.amountFrom) }}
                    <button @click="filters.amountFrom = ''">×</button>
                </span>

                <span v-if="filters.amountTo" class="filter-tag">
                    {{ $t('employer.customerFactors.amountTo') }}: {{ formatCurrency(filters.amountTo) }}
                    <button @click="filters.amountTo = ''">×</button>
                </span>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>{{ $t('employer.customerFactors.loading') }}</span>
        </div>

        <!-- Accounting Grid -->
        <div v-if="filteredData.length > 0" class="accounting-section">
            <div class="accounting-grid">

                <div class="accounting-card" v-for="(record, index) in paginatedData" :key="index">

                    <div class="card-header">
                        <div class="record-detail">
                            <span class="record-date">
                                {{ formatPersianDate(record.Dates) }} - {{ record.Exhibit_Auto }}
                            </span>
                        </div>
                    </div>

                    <div class="card-body">

                        <div class="record-detail">
                            <div class="comment-section">
                                <span class="detail-value">{{ record.Comment2?.trim() || $t('employer.customerFactors.noComment') }}</span>
                            </div>

                            <div class="amount-section">

                                <!-- Debit / Credit -->
                                <span
                                    v-if="selectedCurrency==1"
                                    class="record-amount"
                                    :class="{ 'debit': record.debitPrice, 'credit': record.creditPrice }"
                                >
                                    {{ formatCurrency(record.debitPrice || record.creditPrice) }}
                                    <span>{{record.ExhibitItem_arz2}}</span>
                                </span>

                                <span
                                    v-else
                                    class="record-amount"
                                    :class="{ 'debit': record.debitPrice, 'credit': record.creditPrice }"
                                >
                                    {{ formatCurrency(record.DebitPrice_A || record.creditPrice_A) }}
                                    <span>{{record.ExhibitItem_arz2}}</span>
                                </span>

                                <!-- Badge -->
                                <span class="transaction-badge" :class="record.debitPrice ? 'debit-badge' : 'credit-badge'">
                                    {{ record.debitPrice ? $t('employer.customerFactors.bad') : $t('employer.customerFactors.bas') }}
                                </span>
                            </div>
                        </div>

                        <div class="record-detail" v-if="record.St">
                            <span class="detail-label">{{ $t('employer.customerFactors.description') }}:</span>
                            <span class="detail-value">{{ record.St }}</span>
                        </div>

                        <button
                            v-if="record.ID_Invoice"
                            class="details-btn"
                            @click="openCommercialDetails(record)"
                            :disabled="commercialLoading"
                        >
                            {{ commercialLoading && selectedInvoice === record.ID_Invoice
                                ? $t('employer.customerFactors.loadingDetails')
                                : $t('employer.customerFactors.details') }}
                        </button>
                    </div>
                </div>

            </div>

            <!-- Pagination -->
            <div class="pagination-controls">
                <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
                    {{ $t('employer.customerFactors.prev') }}
                </button>

                <span class="page-info">
                    {{ $t('employer.customerFactors.page') }} {{ currentPage }}
                    {{ $t('employer.customerFactors.of') }}
                    {{ totalPages }}
                </span>

                <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
                    {{ $t('employer.customerFactors.next') }}
                </button>
            </div>
        </div>

        <!-- No Search Results -->
        <div
            v-if="!loading && filteredData.length === 0 && accountingData.length > 0"
            class="empty-state"
        >
            <span>{{ $t('employer.customerFactors.noSearchResults', { query: searchQuery }) }}</span>
        </div>

        <!-- No Records -->
        <div v-if="!loading && accountingData.length === 0" class="empty-state">
            <span>{{ $t('employer.customerFactors.noRecords') }}</span>
        </div>

        <!-- Commercial Modal -->
        <div v-if="showCommercialModal" class="modal-overlay" @click="closeCommercialModal">
            <div class="modal-content" @click.stop>

                <div class="modal-header">
                    <h3>{{ $t('employer.customerFactors.commercialDetails') }}</h3>

                    <div class="modal-actions">

                        <button
                            v-if="commercialDetails.length > 0 && !commercialLoading && !commercialError"
                            class="print-modal-btn"
                            @click="printCommercialDetails"
                        >
                            🖨️ {{ $t('employer.customerFactors.print') }}
                        </button>

                        <button class="close-btn" @click="closeCommercialModal">×</button>
                    </div>
                </div>

                <div class="modal-body">

                    <!-- Loading -->
                    <div v-if="commercialLoading" class="modal-loading">
                        <div class="loading-spinner"></div>
                        <span>{{ $t('employer.customerFactors.loadingCommercial') }}</span>
                    </div>

                    <!-- Error -->
                    <div v-else-if="commercialError" class="modal-error">
                        <div class="error-icon">⚠️</div>
                        <h4>{{ $t('employer.customerFactors.error') }}</h4>
                        <p>{{ commercialError }}</p>

                        <button class="retry-btn" @click="retryFetchCommercialDetails">
                            {{ $t('employer.customerFactors.retry') }}
                        </button>
                    </div>

                    <!-- Success -->
                    <div v-else-if="commercialDetails.length > 0" class="commercial-details">

                        <div class="details-grid">

                            <div class="detail-card" v-for="(detail, index) in commercialDetails" :key="index">

                                <!-- Title: کالا -->
                                <div class="detail-section">
                                    <h4 class="section-title">{{ $t('employer.customerFactors.itemTitle') }}</h4>

                                    <div class="info-box">

                                        <div class="info-row">
                                            <span class="info-label">{{ $t('employer.customerFactors.productName') }}:</span>
                                            <span class="info-value">{{ detail.Name || detail.KalaName || $t('employer.customerFactors.unknown') }}</span>
                                        </div>

                                        <!-- آماری -->
                                        <div class="info-row" v-if="getItemsWithLabels(detail, '13').length">
                                            <span class="info-label">{{ $t('employer.customerFactors.amari') }}:</span>

                                            <div class="multi-values">
                                                <span
                                                    v-for="item in getItemsWithLabels(detail, '13')"
                                                    :key="item.key"
                                                    class="multi-value-item"
                                                >
                                                    {{ item.label }}: {{ formatNumber(item.value) }}
                                                </span>
                                            </div>
                                        </div>

                                        <div class="info-row">
                                            <span class="info-label">{{ $t('employer.customerFactors.unit') }}:</span>
                                            <span class="info-value">{{ detail.vahed || $t('employer.customerFactors.unknown') }}</span>
                                        </div>

                                    </div>
                                </div>

                                <!-- مقادیر -->
                                <div class="detail-section">
                                    <h4 class="section-title">{{ $t('employer.customerFactors.values') }}</h4>

                                    <div class="info-box">

                                        <div class="info-row" v-if="detail.GM7">
                                            <span class="info-label">{{ $t('employer.customerFactors.weightFull') }}:</span>
                                            <span class="info-value">{{ formatNumber(detail.GM7) }}</span>
                                        </div>

                                        <div class="info-row" v-if="detail.GM8">
                                            <span class="info-label">{{ $t('employer.customerFactors.weightEmpty') }}:</span>
                                            <span class="info-value">{{ formatNumber(detail.GM8) }}</span>
                                        </div>
                                        
                                        <div class="info-row" v-if="detail.GM6">
                                            <span class="info-label">{{ $t('employer.customerFactors.gross') }}:</span>
                                            <span class="info-value">{{ formatNumber(detail.GM6) }}</span>
                                        </div>

                                        <!-- کسورات -->
                                        <div class="info-row" v-if="getItemsWithLabels(detail, '11').length">
                                            <span class="info-label">{{ $t('employer.customerFactors.deductions') }}:</span>

                                            <div class="multi-values">
                                                <span
                                                    v-for="item in getItemsWithLabels(detail, '11')"
                                                    :key="item.key"
                                                    class="multi-value-item"
                                                >
                                                    {{ item.label }}: {{ formatNumber(item.value) }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- اضافات -->
                                        <div class="info-row" v-if="getItemsWithLabels(detail, '12').length">
                                            <span class="info-label">{{ $t('employer.customerFactors.additions') }}:</span>

                                            <div class="multi-values">
                                                <span
                                                    v-for="item in getItemsWithLabels(detail, '12')"
                                                    :key="item.key"
                                                    class="multi-value-item"
                                                >
                                                    {{ item.label }}: {{ formatNumber(item.value) }}
                                                </span>
                                            </div>
                                        </div>

                                        <div class="info-row" v-if="detail.value1">
                                            <span class="info-label">{{ $t('employer.customerFactors.net') }}:</span>
                                            <span class="info-value">{{ formatNumber(detail.value1) }}</span>
                                        </div>

                                    </div>
                                </div>

                                <!-- مبالغ -->
                                <div class="detail-section">
                                    <h4 class="section-title">{{ $t('employer.customerFactors.amounts') }}</h4>

                                    <div class="info-box">

                                        <div class="info-row">
                                            <span class="info-label">{{ $t('employer.customerFactors.unitPrice') }}:</span>
                                            <span class="info-value amount">
                                                {{ selectedCurrency == 1
                                                    ? formatCurrency(detail.Fe)
                                                    : formatCurrency(detail.Fe_A) }}
                                            </span>
                                        </div>

                                        <div class="info-row">
                                            <span class="info-label">{{ $t('employer.customerFactors.totalAmount') }}:</span>
                                            <span class="info-value amount total">
                                                {{ selectedCurrency == 1
                                                    ? formatCurrency(detail.money)
                                                    : formatCurrency(detail.money_A) }}
                                            </span>
                                        </div>

                                    </div>
                                </div>

                                <!-- Info -->
                                <div class="detail-section" v-if="hasAdditionalInfo(detail)">
                                    <h4 class="section-title">{{ $t('employer.customerFactors.additionalInfo') }}</h4>

                                    <div class="info-box">

                                        <div class="info-row" v-if="detail.Comment2 && detail.Comment2 !== '-'">
                                            <span class="info-label">{{ $t('employer.customerFactors.comment2') }}:</span>
                                            <span class="info-value">{{ detail.Comment2 }}</span>
                                        </div>

                                        <div class="info-row" v-if="detail.Comment && detail.Comment !== '-'">
                                            <span class="info-label">{{ $t('employer.customerFactors.comment') }}:</span>
                                            <span class="info-value">{{ detail.Comment }}</span>
                                        </div>

                                        <div class="info-row" v-if="detail.Codeing">
                                            <span class="info-label">{{ $t('employer.customerFactors.codeing') }}:</span>
                                            <span class="info-value">{{ detail.Codeing }}</span>
                                        </div>

                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>

                    <!-- Empty -->
                    <div v-else class="modal-empty">
                        <div class="empty-icon">📦</div>
                        <h4>{{ $t('employer.customerFactors.noCommercial') }}</h4>
                        <p>{{ $t('employer.customerFactors.noCommercialDesc') }}</p>
                    </div>
                </div>

                <div class="modal-footer">
                    <button class="close-modal-btn" @click="closeCommercialModal">
                        {{ $t('employer.customerFactors.close') }}
                    </button>
                </div>

            </div>
        </div>

    </div>
</template>
<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { accountingService, employerService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import DatePicker from 'vue3-persian-datetime-picker'

export default {
  name: 'FactorView',
  components: {
    Navbar,
    DatePicker 
  },
  setup() {
    const authStore = useAuthStore()
    const remainingCash = ref(0)
    const nameCustomer = ref('')
    const telCustomer = ref('')
    const accountingData = ref([])
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const currentPage = ref(1)
    const itemsPerPage = 10
    const showCommercialModal = ref(false)
    const commercialDetails = ref([])
    const commercialLoading = ref(false)
    const commercialError = ref(null)
    const selectedInvoice = ref(null)
    const router = useRouter()

    const sortOrder = ref('desc')

    // Add filter states
    const showFilterPanel = ref(false)
    const filters = ref({
        transactionType: [], // ['debit', 'credit']
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: ''
    })

    const arzList = ref([])
    const selectedCurrency = ref(1) // Default to Rial (ID: 1)
    const loadingArz = ref(false)

    // Fetch all ARZ (currencies)
    const fetchAllArz = async () => {
      loadingArz.value = true
      try {
        console.log('🔧 Fetching all ARZ...')
        const response = await accountingService.getAllArz()
        if (response.data.success) {
          arzList.value = response.data.data
          console.log('✅ ARZ list loaded:', arzList.value)
        } else {
          console.error('❌ Failed to load ARZ:', response.data.message)
        }
      } catch (err) {
        console.error('❌ Error fetching ARZ:', err)
        // You can show a toast notification here if needed
      } finally {
        loadingArz.value = false
      }
    }

    // Handle currency change
    const onCurrencyChange = () => {
      console.log('🔧 Currency changed to:', selectedCurrency.value)
      currentPage.value = 1
      fetchAccountingData()
    }

    // Get currency sign for display
    const getCurrencySign = () => {
      if (selectedCurrency.value === 1) return 'ریال' // Default Rial
      
      const selectedArz = arzList.value.find(arz => arz.idd === selectedCurrency.value)
      return selectedArz ? selectedArz.sign : 'ریال'
    }

    // Filter functions
    const toggleFilterPanel = () => {
      showFilterPanel.value = !showFilterPanel.value
    }

    const closeFilterPanel = () => {
      showFilterPanel.value = false
    }

    const applyFilters = () => {
      currentPage.value = 1
      closeFilterPanel()
    }

    const clearFilters = () => {
      filters.value = {
        transactionType: [],
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: ''
      }
      currentPage.value = 1
    }

    const removeTransactionTypeFilter = () => {
      filters.value.transactionType = []
    }

    // Count active filters
    const activeFilterCount = computed(() => {
      let count = 0
      if (filters.value.transactionType.length > 0) count++
      if (filters.value.dateFrom) count++
      if (filters.value.dateTo) count++
      if (filters.value.amountFrom) count++
      if (filters.value.amountTo) count++
      return count
    })

    const getTransactionTypeText = () => {
      if (filters.value.transactionType.length === 2) return 'همه'
      if (filters.value.transactionType.includes('debit')) return 'بدهکار'
      if (filters.value.transactionType.includes('credit')) return 'بستانکار'
      return ''
    }

    const formatFilterDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('fa-IR')
    }

    // Enhanced filter function
    const filteredData = computed(() => {
      let filtered = accountingData.value

      // Apply search filter
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        filtered = filtered.filter(record => {
          const description = (record.Comment2 || '').toLowerCase()
          const amount = (record.debitPrice || record.creditPrice || 0).toString()
          return description.includes(query) || amount.includes(query)
        })
      }

      // Apply transaction type filter
      if (filters.value.transactionType.length > 0) {
        filtered = filtered.filter(record => {
          if (filters.value.transactionType.includes('debit') && record.debitPrice) return true
          if (filters.value.transactionType.includes('credit') && record.creditPrice) return true
          return false
        })
      }

      // Apply date range filter
      if (filters.value.dateFrom) {
        const fromDate = new Date(filters.value.dateFrom)
        filtered = filtered.filter(record => {
          if (!record.Dates) return false
          const recordDate = new Date(record.Dates)
          return recordDate >= fromDate
        })
      }

      if (filters.value.dateTo) {
        const toDate = new Date(filters.value.dateTo)
        toDate.setHours(23, 59, 59, 999) // End of day
        filtered = filtered.filter(record => {
          if (!record.Dates) return false
          const recordDate = new Date(record.Dates)
          return recordDate <= toDate
        })
      }

      // Apply amount range filter
      if (filters.value.amountFrom) {
        const minAmount = parseFloat(filters.value.amountFrom)
        filtered = filtered.filter(record => {
          const amount = record.debitPrice || record.creditPrice || 0
          return amount >= minAmount
        })
      }

      if (filters.value.amountTo) {
        const maxAmount = parseFloat(filters.value.amountTo)
        filtered = filtered.filter(record => {
          const amount = record.debitPrice || record.creditPrice || 0
          return amount <= maxAmount
        })
      }

      return filtered
    })

    const navigateToDashboard = () => {
      const fromWhere = localStorage.getItem('fromwhere')
      if (fromWhere == 'customerSearch')
        router.push('/employer/customer-search')
      else if (fromWhere == 'reportMande2')
        router.push('/employer/report-mande2')
      else 
        router.push('/employer/dashboard')
    }

    const isAuthenticated = computed(() => authStore.isAuthenticated)

    const toggleSortOrder = () => {
      //accountingData.value = []
      sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
      fetchAccountingData() // Refetch data with new sort order
    }

    // Filter data based on search query
    // const filteredData = computed(() => {
    //   if (!searchQuery.value.trim()) {
    //     return accountingData.value
    //   }
      
    //   const query = searchQuery.value.trim().toLowerCase()
    //   return accountingData.value.filter(record => {
    //     const description = (record.Comment2 || '').toLowerCase()
    //     const amount = (record.debitPrice || record.creditPrice || 0).toString()
        
    //     return description.includes(query) || amount.includes(query)
    //   })
    // })

    // Paginated data
    const paginatedData = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      return filteredData.value.slice(startIndex, endIndex)
    })

    // Total pages
    const totalPages = computed(() => {
      return Math.ceil(filteredData.value.length / itemsPerPage)
    })

    // Pagination methods
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }

    // Reset to first page when search changes
    watch(searchQuery, () => {
      currentPage.value = 1
    })

    // Print functionality
    const printData = () => {
      const printWindow = window.open('', '_blank')
      const tableContent = generatePrintableTable()
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ اطلاعات حسابداری</title>
            <style>
              body { 
                font-family: Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
              }
              h1 { 
                text-align: center; 
                color: #333;
                margin-bottom: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: right;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .debit { color: #e74c3c; }
              .credit { color: #27ae60; }
              @media print {
                body { margin: 0; }
                table { width: 100%; }
              }
            </style>
          </head>
          <body>
            <h1>گزارش تراکنش‌های حسابداری</h1>
            <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
            <div>تعداد رکوردها: ${filteredData.value.length}</div>
            ${tableContent}
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 500)
    }

    const generatePrintableTable = () => {
      return `
        <table>
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>شماره سند</th>
              <th>شرح</th>
              <th>مبلغ</th>
              <th>نوع</th>
              <th>ارز</th>
              <th>توضیحات</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.value.map(record => `
              <tr>
                <td>${record.Dates || '-'}</td>
                <td>${record.Exhibit_Auto || '-'}</td>
                <td>${record.Comment2 || 'بدون شرح'}</td>
                <td class="${record.debitPrice ? 'debit' : 'credit'}">
                  ${formatCurrency(record.debitPrice || record.creditPrice)}
                </td>
                <td>${record.debitPrice ? 'بدهکار' : 'بستانکار'}</td>
                <td>${record.ExhibitItem_arz2 || '-'}</td>
                <td>${record.St || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }

    // Fetch accounting data and remaining cash
    const fetchAccountingData = async () => {
      if (!authStore.user?.customerId) {
        console.error('No customer ID found')
        return
      }

      loading.value = true
      error.value = null

      try {
        // Fetch accounting data with currency and sort parameters
        const id_subcode = localStorage.getItem('selectedCustomer', 0)
        if (!id_subcode || id_subcode == 0){
            router.push('/employer/dashboard')
        }
        console.log("select currency is ", selectedCurrency.value)
        const accountingResponse = await employerService.getAccountingData(
          selectedCurrency.value, // Pass selected currency
          id_subcode,
          sortOrder.value
        )
        if (accountingResponse.data.success) {
          accountingData.value = accountingResponse.data.data || []
        }

        // Fetch remaining cash
        const cashResponse = await employerService.getRemainingCash(selectedCurrency.value, parseInt(id_subcode))
        if (cashResponse.data.success) {
          remainingCash.value = cashResponse.data.remaining_cash || 0
        }

        // Fetch name and tel
        const customerDetailResponse = await employerService.getCustomerProfile(parseInt(id_subcode))
        if (customerDetailResponse.data.success) {
          console.log(customerDetailResponse.data)
          nameCustomer.value = customerDetailResponse.data.first_name
          telCustomer.value = customerDetailResponse.data.tel
        }

        console.log('Accounting data fetched:', accountingData.value.length, 'records')
        console.log('Remaining cash:', remainingCash.value)

      } catch (err) {
        console.error('Error fetching accounting data:', err)
        error.value = err.response?.data?.detail || 'خطا در دریافت اطلاعات'
      } finally {
        loading.value = false
      }
    }


    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR', {

      }).format(amount)
    }

    const formatRemainingCash = (amount) => {
      if (!amount && amount !== 0) return '۰'
      // Use absolute value for display
      const absoluteAmount = Math.abs(amount)
      return new Intl.NumberFormat('fa-IR').format(absoluteAmount).replace('ریال', '') + getCurrencySign()
    }

    const formatBestanKari = (amount) => {
      if (!amount && amount !== 0) return '۰'
      if (amount > 0){
        return 'بدهکار'
      }else {
        return 'بستانکار'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      try {
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('fa-IR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date)
      } catch {
        return dateString
      }
    }

    const formatPersianDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      
      try {
        // Split the date string (format: 1404/06/18)
        const parts = dateString.split('/')
        if (parts.length !== 3) return dateString
        
        const year = parts[0]
        const month = parseInt(parts[1])
        const day = parts[2]
        
        // Persian month names
        const persianMonths = [
          'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
          'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        if (month >= 1 && month <= 12) {
          return `${day} ${persianMonths[month - 1]} ${year}`
        }
        
        return dateString
      } catch {
        return dateString
      }
    }

    const openCommercialDetails = async (invoiceAuto) => {
      selectedInvoice.value = invoiceAuto.ID_Invoice
      showCommercialModal.value = true
      commercialLoading.value = true
      commercialError.value = null
      commercialDetails.value = []

      try {
        console.log('Fetching commercial details for invoice:', invoiceAuto)
        const response = await accountingService.getCommercialDetails(invoiceAuto.ID_Invoice, invoiceAuto.atf, invoiceAuto.Ac, invoiceAuto.Act)
        
        if (response.data.success) {
          commercialDetails.value = response.data.data
          console.log('Commercial details loaded:', commercialDetails.value)
        } else {
          commercialError.value = response.data.message || 'خطا در دریافت اطلاعات جزئیات'
        }
      } catch (err) {
        console.error('Error fetching commercial details:', err)
        
        // Handle specific error cases
        if (err.response?.data?.message) {
          commercialError.value = err.response.data.message
        } else if (err.code === 'NETWORK_ERROR') {
          commercialError.value = 'خطا در اتصال به سرور'
        } else {
          commercialError.value = 'خطای ناشناخته در دریافت اطلاعات'
        }
      } finally {
        commercialLoading.value = false
      }
    }

    const closeCommercialModal = () => {
      showCommercialModal.value = false
      commercialDetails.value = []
      commercialError.value = null
      selectedInvoice.value = null
    }

    const retryFetchCommercialDetails = () => {
      if (selectedInvoice.value) {
        openCommercialDetails(selectedInvoice.value)
      }
    }

    const hasAdditionalInfo = (detail) => {
      return (detail.Comment2 && detail.Comment2 !== '-') || 
             (detail.Comment && detail.Comment !== '-') || 
             detail.Codeing
    }

    const formatNumber = (number) => {
      if (!number && number !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    }

    onMounted(async () => {
      if (isAuthenticated.value) {
        console.log("User is authenticated, fetching accounting data...")
        await fetchAllArz()
        await fetchAccountingData()
      }
    })

    const printCommercialDetails = () => {
      const printWindow = window.open('', '_blank')
      const tableContent = generateCommercialDetailsTable()
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ جزئیات بازرگانی - فاکتور ${selectedInvoice.value}</title>
            <style>
              body { 
                font-family: Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
                line-height: 1.6;
              }
              .print-header { 
                text-align: center; 
                color: #333;
                margin-bottom: 30px;
                padding-bottom: 15px;
                border-bottom: 2px solid #667eea;
              }
              .print-header h1 {
                margin: 0 0 10px 0;
                color: #2d3436;
              }
              .print-info {
                display: flex;
                justify-content: space-between;
                margin-bottom: 20px;
                padding: 15px;
                background: #f8f9fa;
                border-radius: 8px;
                border: 1px solid #e0e0e0;
              }
              .info-item {
                display: flex;
                flex-direction: column;
              }
              .info-label {
                font-weight: bold;
                color: #555;
                margin-bottom: 5px;
              }
              .info-value {
                color: #333;
              }
              .details-table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 20px;
                font-size: 14px;
              }
              .details-table th {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                padding: 12px 8px;
                text-align: right;
                border: 1px solid #ddd;
                font-weight: bold;
              }
              .details-table td {
                padding: 10px 8px;
                border: 1px solid #ddd;
                text-align: right;
              }
              .details-table tr:nth-child(even) {
                background-color: #f8f9fa;
              }
              .details-table tr:hover {
                background-color: #e9ecef;
              }
              .amount-cell {
                font-weight: bold;
                color: #2d3436;
              }
              .total-row {
                background-color: #e3f2fd !important;
                font-weight: bold;
              }
              .section-header {
                background-color: #495057;
                color: white;
                font-weight: bold;
                text-align: center;
              }
              .text-center {
                text-align: center;
              }
              .text-left {
                text-align: left;
              }
              @media print {
                body { 
                  margin: 0; 
                  font-size: 12px;
                }
                .print-header {
                  margin-bottom: 20px;
                }
                .details-table {
                  font-size: 11px;
                }
                .details-table th,
                .details-table td {
                  padding: 6px 4px;
                }
                .no-print {
                  display: none !important;
                }
              }
              @page {
                margin: 1cm;
                size: A4 portrait;
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1>گزارش جزئیات بازرگانی</h1>
              <div>فاکتور شماره: ${selectedInvoice.value}</div>
            </div>
            
            <div class="print-info">
              <div class="info-item">
                <span class="info-label">تاریخ چاپ:</span>
                <span class="info-value">${new Date().toLocaleDateString('fa-IR')}</span>
              </div>
              <div class="info-item">
                <span class="info-label">تعداد آیتم‌ها:</span>
                <span class="info-value">${commercialDetails.value.length} مورد</span>
              </div>
              <div class="info-item">
                <span class="info-label">مجموع مبالغ:</span>
                <span class="info-value">${formatCurrency(commercialDetails.value.reduce((sum, item) => sum + (item.Money || 0), 0))}</span>
              </div>
            </div>
            
            ${tableContent}
            
            <div style="margin-top: 30px; text-align: center; color: #666; font-size: 12px;">
              <p>الماس هوشمند - سیستم حسابداری پیشرفته</p>
              <p>تاریخ تولید گزارش: ${new Date().toLocaleString('fa-IR')}</p>
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print()
      }, 500)
    }

    const generateCommercialDetailsTable = () => {
      if (!commercialDetails.value.length) return '<p>هیچ داده‌ای برای نمایش وجود ندارد</p>'
      
      // Helper function to get amari items
      const getAmariItems = (detail) => {
        return getItemsWithLabels(detail, '13').map(item => 
          `${item.label}: ${formatNumber(item.value)}`
        ).join(' | ')
      }
      
      // Helper function to get ksorat items
      const getKsoratItems = (detail) => {
        return getItemsWithLabels(detail, '11').map(item => 
          `${item.label}: ${formatNumber(item.value)}`
        ).join(' | ')
      }
      
      // Function to format weight (container)
      const formatWeightZarf = (detail) => {
        const ksoratItems = getItemsWithLabels(detail, '11')
        const ezafatItems = getItemsWithLabels(detail, '12')
        
        // Find weight-related items
        const ksoratWeight = ksoratItems.find(item => 
          item.label.includes('وزن') || item.label.includes('ظرف')
        )
        const ezafatWeight = ezafatItems.find(item => 
          item.label.includes('وزن') || item.label.includes('ظرف')
        )
        
        if (ksoratWeight) return ksoratWeight.value
        if (ezafatWeight) return ezafatWeight.value
        return '-'
      }
      
      return `
        <table class="details-table">
          <thead>
            <tr>
              <th width="15%">عنوان کالا</th>
              <th width="8%">واحد</th>
              <th width="10%">آماری</th>
              <th width="8%">وزن پر</th>
              <th width="8%">وزن خالی</th>
              <th width="8%">مقدار ناخالص</th>
              <th width="8%">کسورات</th>
              <th width="8%">وزن ظرف</th>
              <th width="8%">مقدار خالص</th>
              <th width="10%">فی (${getCurrencySign()})</th>
              <th width="9%">مبلغ کل (${getCurrencySign()})</th>
            </tr>
          </thead>
          <tbody>
            ${commercialDetails.value.map((detail) => {
              const feValue = selectedCurrency.value == 1 ? detail.Fe : detail.Fe_A
              const moneyValue = selectedCurrency.value == 0 ? detail.money : detail.money_A
              
              return `
                <tr>
                  <!-- Title -->
                  <td><strong>${detail.Name || 'نامشخص'}</strong></td>
                  
                  <!-- Unit -->
                  <td class="text-center">${detail.vahed || '-'}</td>
                  
                  <!-- Amari -->
                  <td class="text-center">
                    ${getItemsWithLabels(detail, '13').length > 0 ? getAmariItems(detail) : '-'}
                  </td>
                  
                  <!-- Weight Full -->
                  <td class="text-center">${detail.GM7 ? formatNumber(detail.GM7) : '-'}</td>
                  
                  <!-- Weight Empty -->
                  <td class="text-center">${detail.GM8 ? formatNumber(detail.GM8) : '-'}</td>
                  
                  <!-- Gross Amount -->
                  <td class="text-center">${detail.GM6 ? formatNumber(detail.GM6) : '-'}</td>
                  
                  <!-- Ksorat -->
                  <td class="text-center">
                    ${getItemsWithLabels(detail, '11').length > 0 ? getKsoratItems(detail) : '-'}
                  </td>
                  
                  <!-- Container Weight -->
                  <td class="text-center">${formatWeightZarf(detail)}</td>
                  
                  <!-- Net Amount -->
                  <td class="text-center">${detail.value1 ? formatNumber(detail.value1) : '-'}</td>
                  
                  <!-- Unit Price -->
                  <td class="amount-cell">${feValue ? formatCurrency(feValue) : '-'}</td>
                  
                  <!-- Total Amount -->
                  <td class="amount-cell" style="color: #e74c3c;">
                    <strong>${moneyValue ? formatCurrency(moneyValue) : '-'}</strong>
                  </td>
                </tr>
              `
            }).join('')}
            
            <!-- Total Row -->
            <tr class="total-row">
              <td colspan="9" style="text-align: left;">
                <strong>جمع کل:</strong>
              </td>
              <td class="amount-cell">
                ${formatCurrency(commercialDetails.value.reduce((sum, item) => {
                  const fe = selectedCurrency.value == 1 ? item.Fe : item.Fe_A
                  return sum + (fe || 0)
                }, 0))}
              </td>
              <td class="amount-cell" style="color: #e74c3c;">
                <strong>
                  ${formatCurrency(commercialDetails.value.reduce((sum, item) => {
                    const money = selectedCurrency.value == 0 ? item.Money : item.Money_A
                    return sum + (money || 0)
                  }, 0))}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Additional Information Section -->
        ${generateAdditionalInfoTable()}
      `
    }

    const generateAdditionalInfoTable = () => {
      const itemsWithAdditionalInfo = commercialDetails.value.filter(item => 
        (item.Comment2 && item.Comment2 !== '-') || 
        (item.Comment && item.Comment !== '-') || 
        item.Codeing
      )
      
      if (itemsWithAdditionalInfo.length === 0) return ''
      
      return `
        <div style="margin-top: 30px;">
          <h3 style="color: #333; border-bottom: 2px solid #667eea; padding-bottom: 8px;">اطلاعات تکمیلی</h3>
          <table class="details-table">
            <thead>
              <tr>
                <th width="25%">عنوان کالا</th>
                <th width="15%">کدینگ</th>
                <th width="30%">شرح</th>
                <th width="30%">شرح ۲</th>
              </tr>
            </thead>
            <tbody>
              ${itemsWithAdditionalInfo.map(item => `
                <tr>
                  <td><strong>${item.Name || 'نامشخص'}</strong></td>
                  <td class="text-center">${item.Codeing || '-'}</td>
                  <td>${item.Comment && item.Comment !== '-' ? item.Comment : '-'}</td>
                  <td>${item.Comment2 && item.Comment2 !== '-' ? item.Comment2 : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `
    }

    const getNumbericalItems = (prefix) => {
        if (!commercialDetails.value.length) return []
        
        const currentDetail = commercialDetails.value[0]
        const items = []
        
        for (const [key, value] of Object.entries(currentDetail)) {
            if (key.startsWith(prefix) && value && value !== 0) {
                items.push({ key, value })
            }
        }
        
        return items
    }

    const getItemsWithLabels = (detail, prefix) => {
      const items = []
      const fieldLabels = detail._field_labels || {}
      
      for (const [key, value] of Object.entries(detail)) {
          if (key.startsWith(prefix) && value && value !== 0) {
              const label = fieldLabels[key] || key // Use label if available, otherwise use key
              items.push({ key, value, label })
          }
      }
      
      return items
    }

    return {
      nameCustomer,
      telCustomer,

      arzList,
      selectedCurrency,
      loadingArz,
      getCurrencySign,
      onCurrencyChange,
      sortOrder,
      showFilterPanel,
      remainingCash,
      accountingData,
      filteredData,
      filters,
      activeFilterCount,
      paginatedData,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      isAuthenticated,
      showCommercialModal,
      commercialDetails,
      commercialLoading,
      commercialError,
      selectedInvoice,
      getNumbericalItems,
      getItemsWithLabels,
      formatCurrency,
      formatBestanKari,
      formatPersianDate,
      formatDate,
      nextPage,
      prevPage,
      printData,
      openCommercialDetails,
      closeCommercialModal,
      retryFetchCommercialDetails,
      hasAdditionalInfo,
      formatNumber,
      printCommercialDetails,
      toggleFilterPanel,
      closeFilterPanel,
      applyFilters,
      clearFilters,
      removeTransactionTypeFilter,
      getTransactionTypeText,
      formatFilterDate,
      navigateToDashboard,
      toggleSortOrder,
      formatRemainingCash,
      truncateText: (text, maxLength) => {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
      }
    }
  }
}
</script>
<style scoped>
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  direction: rtl;
}
.account-summary-container {
  position: sticky;
  top: 70px; /* Adjust this value based on your navbar height */
  z-index: 100;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 100%;
}
.account-summary {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: white;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 300px;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.account-type {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.account-balance {
  display: flex;
  align-items: center;
  gap: 8px;
}

.balance-amount {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

/* Second row styles */
.second-row {
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  padding-top: 12px;
  margin-top: 4px;
}

.customer-info {  align-items: center;
  gap: 8px;
}

.tel-info {
  margin-left: 20px; /* Adjust spacing between name and tel */
}

.info-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.info-value {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  direction: ltr; /* For phone numbers to display correctly */
  unicode-bidi: plaintext; /* For mixed RTL/LTR text */
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.print-btn {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.print-btn:hover {
  background: #34495e;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

/* Loading State */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.modal-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.modal-error {
  text-align: center;
  padding: 40px;
  color: #d63031;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-error h4 {
  margin: 0 0 10px 0;
  color: #d63031;
}

.modal-error p {
  margin: 0 0 20px 0;
  color: #666;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.2s;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-empty h4 {
  margin: 0 0 10px 0;
  color: #333;
}

/* Commercial Details Styles */
.commercial-details {
  margin-top: 10px;
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.info-box {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
}

        /* Multi-values styling */
        .multi-values {
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
        }
        
        .multi-value-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0;
        }
        
        .multi-value-code {
            font-weight: 600;
            color: #4b6cb7;
            margin-left: 10px;
        }
        
        .multi-value-amount {
            color: #2c3e50;
            font-weight: 500;
        }

.info-value.amount {
  font-weight: 600;
  color: #2d3436;
}

.info-value.amount.total {
  color: #e74c3c;
  font-size: 1rem;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.close-modal-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .modal-header {
    padding: 15px;
  }
  
  .modal-body {
    padding: 15px;
  }
  
  .detail-card {
    padding: 15px;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .info-label, .info-value {
    font-size: 0.85rem;
  }
}
.modal-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.print-modal-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
}

/* Update modal header to accommodate the print button */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Ensure the header content is properly aligned */
.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  flex: 1;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .modal-actions {
    gap: 8px;
  }
  
  .print-modal-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .modal-header {
    padding: 15px;
  }
}
.accounting-section {
  margin-bottom: 45px;
  background-color: var(--bg);
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  /*padding-bottom: 12px;
   border-bottom: 1px solid #f0f0f0; */
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}



.debit-badge {
  background-color: #e74c3c; /* Red for بد */
}

.credit-badge {
  background-color: #27ae60; /* Green for بس */
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: #e74c3c;
}

.credit {
  color: #27ae60;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.comment-section {
  flex: 1;
  min-width: 0; /* Important for text truncation */
  text-align: right; /* For RTL languages */
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: right; /* For RTL */
  word-wrap: break-word;
  display: block;
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
}
.sort-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.sort-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.sort-text {
  font-size: 12px;
  color: #495057;
}

.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Filter Button */
.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  position: relative;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.filter-header h4 {
  margin: 0;
  color: #495057;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
}

.filter-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #495057;
}

.filter-options {
  display: flex;
  gap: 15px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-option input[type="checkbox"] {
  margin: 0;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 15px;
}

.date-input, .amount-input {
  flex: 1;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #6c757d;
}

.date-picker, .amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-tag button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-container{
    width: 100%;
  }
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
  
  .date-inputs, .amount-inputs {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .active-filter-tags {
    flex-direction: column;
  }
}
.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 15px;
}

.currency-label {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  min-width: 120px;
}

.currency-select:disabled {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.currency-loading {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
}

/* Responsive design for currency selector */
@media (max-width: 768px) {
  .currency-selector {
    margin-right: 10px;
  }
  
  .currency-select {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .currency-label {
    font-size: 0.8rem;
  }
}

/* Update the action-bar to accommodate the new currency selector */
.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Ensure proper spacing in mobile view */
@media (max-width: 768px) {
  .action-bar {
    gap: 8px;
    padding: 10px;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
  width: 120px; /* Fixed width for perfect alignment */
  text-align: right;
  flex-shrink: 0;
  margin-left: 10px;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  padding-right: 15px;
}

/* Mobile styles */
@media (max-width: 768px) {
    .info-row {
        flex-direction: row;
        align-items: center;
        padding: 10px 0;
    }
    
    .info-label {
        width: 100px;
        text-align: right;
        margin-left: 5px;
        margin-bottom: 0;
        font-size: 0.85rem;
    }
    
    .info-value {
        width: auto;
        text-align: right;
        padding-right: 5px;
        font-size: 0.85rem;
        font-weight: 500;
        flex: 1;
    }
}

.duration-section {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.duration-item {
  display: inline;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 5px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.duration-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
}

.duration-value {
  font-size: 14px;
  font-weight: 700;
  color: #495057;
}
.account-summary-container {
  position: fixed;
  top: 70px;
  left: 20px; /* or right: 20px for RTL */
  z-index: 100;
  width: 320px;
  margin-bottom: 0;
}

/* Add padding to your main content to prevent overlap */
.main-content {
  padding-left: 350px; /* Adjust based on width of fixed container */
}

/* Mobile */
@media (max-width: 768px) {
  .account-summary-container {
    position: sticky;
    top: 70px;
    left: 0;
    width: 100%;
    padding: 0 10px;
  }
  
  .main-content {
    padding-left: 0;
  }
}
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
  direction: rtl;
}

/* ACCOUNT SUMMARY */
.account-summary-container {
  position: fixed;
  top: 70px;
  left: 20px;
  z-index: 100;
  width: 320px;
  margin-bottom: 0;
  transition: all .3s ease;
}

@media(max-width:768px){
  .account-summary-container {
    position: sticky;
    left: 0;
    width: 100%;
    padding: 0 10px;
  }
}

.account-summary {
  background: var(--primary);
  color: var(--text-light, #fff);
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.account-type {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-light);
  opacity: .9;
}

.second-row {
  border-top: 1px solid rgba(255,255,255,.2);
  padding-top: 12px;
  margin-top: 4px;
}

.info-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-light);
  opacity: .9;
}

.info-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-light);
  direction: ltr;
}

/* SECTION HEADER */
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--text);
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text);
  padding: 5px 10px;
  border-radius: 4px;
  transition: .3s;
}

.back-arrow:hover {
  background: var(--card-bg);
}

/* ACTION BAR */
.action-bar {
  background: var(--card-bg);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  box-shadow: var(--shadow-sm);
  margin-bottom: 15px;
}

/* PRINT BUTTON */
.print-btn {
  background: var(--primary-dark);
  color: var(--text-light);
  padding: 10px 15px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.print-btn:hover {
  background: var(--primary);
}

/* SEARCH */
.search-container { position: relative; width: 300px; }
.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--input-text);
  border-radius: 8px;
}
.search-icon { color: var(--text-muted); }

/* SORT BUTTON */
.sort-btn, .filter-btn {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  color: var(--text);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  transition: .3s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sort-btn:hover, .filter-btn:hover {
  background: var(--input-bg);
}

/* FILTER BADGE */
.filter-badge {
  background: var(--red);
  color: var(--text-light);
}

/* FILTER PANEL */
.filter-panel {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  box-shadow: var(--shadow-md);
}

.filter-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--card-border);
}

.close-filter-btn { color: var(--text); }

.filter-label { color: var(--text); }
.date-picker, .amount-picker {
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--input-text);
}

/* ACTIVE FILTERS */
.active-filters {
  background: var(--primary-light);
  border: 1px solid var(--primary);
}

.filter-tag {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  color: var(--text);
}

/* ACCOUNTING GRID */
.accounting-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
  border-radius: 12px;
  padding: 16px;
  transition: .2s;
}

.accounting-card:hover {
  box-shadow: var(--shadow-md);
}

.record-date { color: var(--text-muted); }

.record-amount.debit {
  color: var(--red);
}
.record-amount.credit {
  color: var(--green);
}

/* BADGES */
.debit-badge {
  background: var(--red);
  color: var(--text-light);
}

.credit-badge {
  background: var(--green);
  color: var(--text-light);
}

/* LOADING */
.loading-state {
  color: var(--text-muted);
}

.loading-spinner {
  border: 3px solid var(--input-bg);
  border-top: 3px solid var(--primary);
}

/* EMPTY STATE */
.empty-state {
  color: var(--text-muted);
  background: var(--card-bg);
  border: 1px dashed var(--card-border);
}

/* MODAL */
.modal-overlay {
  background: var(--overlay);
}

.modal-content {
  background: var(--card-bg);
  box-shadow: var(--shadow-lg);
}

.modal-header {
  background: var(--primary);
  color: var(--text-light);
  border-bottom: 1px solid var(--primary-dark);
}

.close-btn {
  color: var(--text-light);
}

.modal-body { background: var(--card-bg); }

.modal-footer {
  border-top: 1px solid var(--card-border);
}

.modal-error {
  color: var(--red);
}

.retry-btn {
  background: var(--primary);
  color: var(--text-light);
}

.print-modal-btn {
  background: var(--green);
  color: var(--text-light);
}

/* INFO BOXES */
.detail-card {
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  box-shadow: var(--shadow-sm);
}

.info-box {
  background: var(--bg);
  border: 1px solid var(--card-border);
}

.info-label { color: var(white); }
.info-value { color: var(white); }

.info-value.amount.total { color: var(--red); }

/* CURRENCY SELECTOR */
.currency-label { color: var(--text); }
.currency-select {
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--input-text);
}
.currency-loading { color: var(--text-muted); }
</style>